import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.css';
import img1 from '../../../src/images/left-white.svg';
import img2 from '../../../src/images/7-days-icon.svg';
import img8 from '../../../src/images/home2.svg';
import img9 from '../../../src/images/Icon (5).svg';
import img10 from '../../../src/images/notification.svg';
import img11 from '../../../src/images/Calendar3.svg';
import img12 from '../../../src/images/Icon (6).svg';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import config from '../../config/config';


function GlucoseTrends() {
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [weeklyStats, setWeeklyStats] = useState({ avgGlucose: "Loading...", timeInRange: "Loading...", readings: 0 });
    const [dailyGlucoseData, setDailyGlucoseData] = useState([]);
    const [insulinVsGlucoseData, setInsulinVsGlucoseData] = useState([]);
    const [timeRange, setTimeRange] = useState(7);
    const [dateRange, setDateRange] = useState('');
    const [selectedRange, setSelectedRange] = useState(7); // Default range is 7 days
    const backendURL = `${config.backendServerHost}`;

    useEffect(() => {
            fetch(`${backendURL}/patients/api/patients`)
            .then((res) => res.json())
            .then((data) => {
                setPatients(data);
                if (data.length > 0) setSelectedPatient(data[0]);
            })
            .catch((error) => console.error('Error fetching patients:', error));
    }, [backendURL]);

    useEffect(() => {
        if (selectedPatient) {
            // Calculate date range based on timeRange
            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(today.getDate() - timeRange + 1);

            const formattedStart = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const formattedEnd = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            setDateRange(`${formattedStart} - ${formattedEnd}`);

            fetch(`${backendURL}/patients/api/glucose-weekly-average/${selectedPatient.id}`)
                .then((res) => res.json())
                .then((data) => {
                    const chartData = data.last7DaysAverages.map((average, index) => ({
                        day: `Day ${index + 1}`,
                        glucose: parseFloat(average),
                    }));
                    setDailyGlucoseData(chartData);
                });


             // Initialize state before fetching
            setWeeklyStats({ avgGlucose: "Loading...", timeInRange: "Loading...", readings: 7 });

            // Fetch time in range
           const fetchTimeInRange = fetch(`${backendURL}/patients/api/time-in-range/${selectedPatient.id}`)
                .then((res) => {
                    if (!res.ok) throw new Error("Failed to fetch time-in-range data");
                   return res.json();
                })
               .then((data) => data.timeInRange);

           // Fetch weekly glucose average
            const fetchWeeklyAverage = fetch(`${backendURL}/patients/api/glucose-weekly-average/${selectedPatient.id}`)
                .then((res) => {
                   if (!res.ok) throw new Error("Failed to fetch glucose weekly average data");
                   return res.json();
               })
               .then((data) => data.weeklyAverage);

           // Combine results from both API calls
           Promise.all([fetchTimeInRange, fetchWeeklyAverage])
              .then(([timeInRange, weeklyAverage]) => {
                   setWeeklyStats({
                        avgGlucose: weeklyAverage || "N/A", // Value from glucose-weekly-average
                        timeInRange:timeInRange || "N/A",  // Value from time-in-range
                       readings: 7               // Hardcoded for weekly stats
                   });
               })
               .catch((error) => {
                    console.error("Error fetching weekly stats:", error);
                   setWeeklyStats({ avgGlucose: "N/A", timeInRange: "N/A", readings: 7 });
               });
        }
    }, [selectedPatient, backendURL]);

const WeeklyStats = ({ weeklyStats }) => (


                <div className='glucose_trend_cnt'>
                    <div className='average_glucose'>
                        <h3>{weeklyStats?.avgGlucose || 'N/A'}</h3>
                        <h4>mg/dL</h4>
                    </div>
                    <div className='average_glucose'>
                        <h3>{weeklyStats?.timeInRange || 'N/A'}</h3>
                        <h4 className='yellow-text'>70-180</h4>
                    </div>
                    <div className='average_glucose'>
                        <h3>{weeklyStats?.readings || 'N/A'}</h3>
                        <h4>Readings</h4>
                    </div>
            </div>
);

const DailyGlucoseGraph = ({ patientId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!patientId) return;

        setLoading(true);

    fetch(`${backendURL}/patients/api/glucose-weekly-average/${patientId}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Failed to fetch weekly glucose data");
                }
                return res.json();
            })
            .then((response) => {
                // Ensure the response contains valid last7DaysAverages data
                if (!response.last7DaysAverages || !Array.isArray(response.last7DaysAverages)) {
                    throw new Error("Invalid data format");
                }

                // Format data for the LineChart
                const formattedData = response.last7DaysAverages.map(({ day, average }) => ({
                    day, // Date in month/day format
                    glucose: parseFloat(average), // Convert string to number
                }));

                setData(formattedData);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [patientId]);

    if (loading) return <div>Loading Daily Glucose Data...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!data || data.length === 0) return <div>No glucose data available</div>;

    return (
        <div className="daily-glucose-graph">
            <h2>Daily Glucose Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="day"
                        label={{ value: "Day", position: "insideBottomRight", offset: 0 }}
                    />
                    <YAxis
                        domain={[0, 200]}
                        label={{ value: "Glucose (mg/dL)", angle: -90, position: "insideLeft" }}
                    />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="glucose"
                        stroke="#8884d8"
                        name="Average Glucose"
                        dot={{ r: 5 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};



    const handleRangeChange = (event) => {
        setSelectedRange(parseInt(event.target.value, 10));
    };

    const calculateDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - selectedRange);
        const options = { month: 'short', day: 'numeric' };

        return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
    };



    return (
        <div className="glucose-main-cantainer">
            <div className='glucose_container'>
                <div className='glucose-navbar'>
                    <div><img src={img1} alt="" /></div>
                    <div><h4>Glucose Trend</h4></div>
                    <div></div>
                </div>
                <div className='days-button'><span><img src={img2} alt="" />
                </span>
                {/* Date Range */}
                <div className="header">
                    <select onChange={handleRangeChange} value={selectedRange}>
                       <option value={7}>7 Days</option>
                        <option value={14}>14 Days</option>
                        <option value={30}>30 Days</option>
                     </select>
                </div>
            </div>
            <div className='date-heading'><h5>{calculateDateRange()}</h5></div>

{selectedPatient && weeklyStats ? (
    <>
        <WeeklyStats weeklyStats={weeklyStats} />
        <DailyGlucoseGraph patientId={selectedPatient.id} />
    </>
) : (
    <div>Loading data or no data available...</div>
)}

            </div>


            <div className='glucose-wrapper'>
                <div className='key-insights-text'>
                    <h2>Key Insights & Recommendations</h2>
                    <div >
                        <h3>Nov 10 15:30</h3>
                        <h4>Range: 128 - 198 mg/dL</h4>
                    </div>
                    <div >
                        <h3>Nov 9 09:35</h3>
                        <h4>Range: 113 - 178 mg/dL</h4>
                    </div>
                    <div >
                        <h3>Nov 8 18:38</h3>
                        <h4>Range: 120 - 178 mg/dL</h4>
                    </div>
                    <div className='recommendation-text'>
                        <div >
                            <h3>Nov 7 15:30</h3>
                            <h4>Range: 122 - 174 mg/dL</h4>
                        </div>
                        <div>
                            <h6>Post-breakfast spike</h6>
                        </div>
                    </div>
                    <div className='recommendation-text'>
                        <div >
                            <h3>Nov 7 15:30</h3>
                            <h4>Bolus: Post-lunch glucose spike</h4>
                        </div>
                        <div>
                            <h6>Overnight dip</h6>
                        </div>
                    </div>
                </div>


                <div className="footer_container_wrapper">
                <div className="footer_container">
                    <div className="footer_icon" onClick={() => navigate('/')}>
                        <img src={img8} alt="Home Icon" />
                    </div>
                    <div className="footer_icon" onClick={() => navigate('/analysis')}>
                        <img src={img9} alt="Analytics Icon" />
                    </div>
                    <div className="footer_icon" onClick={() => navigate('/alerts')}>
                        <img src={img10} alt="Notification Icon" />
                    </div>
                    <div className="footer_icon" onClick={() => navigate('/appointments')}>
                        <img src={img11} alt="Calendar Icon" />
                    </div>
                    <div style={{ cursor: "pointer" }}>
                        <img src={img12} alt="" />
                    </div>
                </div>
            </div>
            
            </div>

            

        </div>
    );
}

export default GlucoseTrends;
